import { z } from 'zod'
import { TimeUnit, toMilliseconds } from '../date-utils/timeConverter'

export const MAX_POLLING_FREQUENCY_DAYS = 30
export const MAX_POLLING_FREQUENCY_HOURS = 720
export const MAX_POLLING_FREQUENCY_MINUTES = 60

// ExtOps applies this same window when the field is absent, so the two must stay in step.
export const DEFAULT_POLLING_EXPIRATION_DAYS = 30
export const DEFAULT_POLLING_EXPIRATION_UNIT = 'days'

const TIME_UNIT_BY_POLLING_UNIT: Record<string, TimeUnit> = {
  minutes: 'minute',
  hours: 'hour',
  days: 'day',
}

export const pollingDurationToMs = (value: number, unit: string): number | undefined => {
  const timeUnit = TIME_UNIT_BY_POLLING_UNIT[unit]

  return timeUnit === undefined ? undefined : toMilliseconds(value, timeUnit)
}

const MAX_POLLING_EXPIRATION_MS = toMilliseconds(365, 'day')

export const getMaxPollingExpirationValue = (unit: string): number | undefined => {
  const oneUnitInMs = pollingDurationToMs(1, unit)

  return oneUnitInMs === undefined ? undefined : MAX_POLLING_EXPIRATION_MS / oneUnitInMs
}

export const isValidPollingFrequency = (value: number | undefined, unit: string): boolean => {
  if (value === undefined || value === null || value <= 0) {
    return false
  }
  if (unit === 'minutes') {
    return value <= MAX_POLLING_FREQUENCY_MINUTES
  }
  if (unit === 'hours') {
    return value <= MAX_POLLING_FREQUENCY_HOURS
  }
  if (unit === 'days') {
    return value <= MAX_POLLING_FREQUENCY_DAYS
  }
  return false
}

export const PollingFrequencyUnitSchema = z.enum(['minutes', 'hours', 'days'])
export type PollingFrequencyUnit = z.infer<typeof PollingFrequencyUnitSchema>

type PollingDuration = { value?: number; unit: string }

export const isValidPollingExpiration = (expiration: PollingDuration | undefined): boolean => {
  if (expiration === undefined) {
    return true
  }

  const unitInMs = pollingDurationToMs(1, expiration.unit)

  if (unitInMs === undefined) {
    return false
  }

  // An unset value means the default window, so only a value that is present has to be in range.
  if (expiration.value === undefined || expiration.value === null) {
    return true
  }

  return Number.isInteger(expiration.value) && expiration.value > 0 && expiration.value * unitInMs <= MAX_POLLING_EXPIRATION_MS
}

// A window shorter than the interval leaves the step expiring before its second poll. An unset
// expiration still has a window — the default one — so it is compared rather than waved through.
export const isPollingExpirationLongerThanFrequency = (
  frequency: PollingDuration | undefined,
  expiration: PollingDuration | undefined
): boolean => {
  if (frequency?.value === undefined) {
    return true
  }

  const effectiveExpiration =
    expiration?.value !== undefined
      ? { value: expiration.value, unit: expiration.unit }
      : { value: DEFAULT_POLLING_EXPIRATION_DAYS, unit: DEFAULT_POLLING_EXPIRATION_UNIT }

  const frequencyMs = pollingDurationToMs(frequency.value, frequency.unit)
  const expirationMs = pollingDurationToMs(effectiveExpiration.value, effectiveExpiration.unit)

  if (frequencyMs === undefined || expirationMs === undefined) {
    return true
  }

  return expirationMs > frequencyMs
}

const PollingFrequencySchema = z.object({
  value: z
    .number()
    .int()
    .positive()
    .describe('Polling interval value. Max 720 hours or 30 days. Must be a positive integer.')
    .optional(),
  unit: PollingFrequencyUnitSchema,
})

const PollingExpirationSchema = z.object({
  value: z
    .number()
    .int()
    .positive()
    .describe('How long to keep polling before the step expires. Max 365 days. Omitted means 30 days.')
    .optional(),
  unit: PollingFrequencyUnitSchema,
})

export const PollingConfigurationSchema = z.object({
  enabled: z.boolean(),
  frequency: PollingFrequencySchema,
  property: z.string().min(1).describe('JSON path to extract from the response (e.g., "$.status" or "ticket.status")'),
  value: z
    .array(z.string().min(1))
    .optional()
    .describe('Expected values that will complete the block when found (e.g., ["completed", "done"])'),
  expiration: PollingExpirationSchema.optional(),
})

export type PollingConfiguration = z.infer<typeof PollingConfigurationSchema>

export const PollingStateSchema = z.object({
  lastPollTimestamp: z.iso.datetime(),
  pollAttemptCount: z.number().int().nonnegative(),
  nextScheduledPollTime: z.iso.datetime(),
  pollingPropertyValue: z.unknown().optional(),
})
